import React, { useState } from 'react'

import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { createUsuario } from '../api/admin.api'

import './add-access.css'
import './add-access-modal.css'

// Simple regex used to validate e-mail format before submission
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Initial state shared between the first render and each reset after close
const INITIAL_FORM = { email: '', cliente_id: '', rol: 'cliente', activo: true }
const INITIAL_ERRORS = { email: '', cliente_id: '' }

const AddAccess = ({ isOpen, onClose, clientes, onSuccess }) => {
  // Controlled form values
  const [formData, setFormData] = useState({ ...INITIAL_FORM })

  // True while the create API call is in-flight
  const [loading, setLoading] = useState(false)

  // Per-field validation error messages; empty string means no error
  const [errors, setErrors] = useState({ ...INITIAL_ERRORS })

  // Updates a single form field and clears its validation error
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  // Validates required fields; populates errors state and returns true/false
  const validateForm = () => {
    const next = { ...INITIAL_ERRORS }
    let valid = true

    if (!formData.email || !EMAIL_REGEX.test(formData.email)) {
      next.email = 'Introduce un correo electrónico válido'
      valid = false
    }

    // cliente_id is required only for the 'cliente' role
    if (formData.rol === 'cliente' && !formData.cliente_id) {
      next.cliente_id = 'Selecciona una empresa'
      valid = false
    }

    setErrors(next)
    return valid
  }

  // Submits the new user to the API after passing validation
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    try {
      await createUsuario({
        email:      formData.email,
        // Admin users are not tied to a client
        cliente_id: formData.rol === 'admin' ? null : Number(formData.cliente_id),
        rol:        formData.rol,
        activo:     formData.activo,
      })
      toast.success('Usuario creado correctamente')
      onSuccess()
      handleClose()
    } catch (err) {
      const code = err?.response?.data?.detail
      // Surface a friendly message for the most common conflict
      const msg = code === 'USUARIO_EMAIL_EXISTS'
        ? 'El email ya está registrado'
        : (code || 'Error al crear el usuario')
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  // Resets form state and closes the modal
  const handleClose = () => {
    setFormData({ ...INITIAL_FORM })
    setErrors({ ...INITIAL_ERRORS })
    onClose()
  }

  // Render nothing when the modal is not open
  if (!isOpen) return null

  return (
    <div className="add-access-modal-overlay" onClick={handleClose}>
      {/* Stop propagation so clicks inside the card don't close the modal */}
      <div className="add-access-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-access-thq-addaccess-elm">
          <div className="add-access-thq-title-elm1">
            <span className="add-access-thq-h1-elm headline01">
              Añadir acceso
            </span>
            <span className="add-access-thq-h2-elm bodystandard">
              Invita a un nuevo usuario
            </span>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="add-access-thq-fields-elm">
              {/* Email field */}
              <div className={`form-field${errors.email ? ' has-error' : ''}`}>
                <span className="add-access-thq-title-elm3 bodysmall">
                  Correo electrónico
                </span>
                <input
                  type="email"
                  placeholder="usuario@empresa.com"
                  className="add-access-thq-emailfield-elm bodystandard"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>

              {/* Rol selector */}
              <div className="form-field">
                <span className="add-access-thq-title-elm2 bodysmall">Rol</span>
                <select
                  value={formData.rol}
                  onChange={(e) => handleChange('rol', e.target.value)}
                  className="bodystandard"
                >
                  <option value="cliente">Cliente</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {/* Cliente selector — disabled and cleared when rol is 'admin' */}
              <div className={`add-access-thq-clientid-elm form-field${errors.cliente_id ? ' has-error' : ''}`}>
                <span className="add-access-thq-title-elm2 bodysmall">
                  Empresa
                </span>
                {formData.rol === 'admin' ? (
                  <span className="bodysmall" style={{ color: '#6B7280' }}>N/A — los administradores no pertenecen a ninguna empresa</span>
                ) : (
                  <select
                    value={formData.cliente_id}
                    onChange={(e) => handleChange('cliente_id', e.target.value)}
                    className="bodystandard"
                  >
                    <option value="">Seleccionar empresa</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre_empresa}</option>
                    ))}
                  </select>
                )}
                {errors.cliente_id && <p className="form-error">{errors.cliente_id}</p>}
              </div>
            </div>

            <button
              type="submit"
              className="add-access-thq-button-elm"
              disabled={loading}
            >
              <span className="add-access-thq-label-elm2">
                {loading ? 'Añadiendo...' : 'Añadir acceso'}
              </span>
            </button>
          </form>
        </div>

        {/* Close button in the top-right corner */}
        <button
          type="button"
          onClick={handleClose}
          className="add-access-modal-close"
          style={{
            lineHeight: 1,
          }}
          aria-label="Cerrar"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

AddAccess.defaultProps = {
  clientes: [],
}

AddAccess.propTypes = {
  // Controls modal visibility
  isOpen:    PropTypes.bool.isRequired,
  // Callback to close the modal
  onClose:   PropTypes.func.isRequired,
  // List of clients to populate the empresa dropdown
  clientes:  PropTypes.array,
  // Called after a successful user creation
  onSuccess: PropTypes.func.isRequired,
}

export default AddAccess
